/**
 * The error contract, shared so the client can branch on a failure without
 * pattern-matching English prose.
 *
 * RFC 7807 `application/problem+json` (spec §13.1): a POS terminal, a mobile
 * app and a browser all parse a failure the same way. `code` is a stable
 * machine-readable string and the only thing a client should branch on.
 *
 * `detail` stays English and human-readable on purpose — it is not a UI string.
 * The web client maps `code` to a translated message, so pre-localising it here
 * would mean translating once in the wrong place and losing the original.
 */

export const ERROR_CODES = [
  'VALIDATION_FAILED',
  'UNAUTHENTICATED',
  'INVALID_CREDENTIALS',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'PRECONDITION_FAILED',
  'PAYLOAD_TOO_LARGE',
  'UNSUPPORTED_MEDIA_TYPE',
  'RATE_LIMITED',
  'VERSION_CONFLICT',
  'PERIOD_CLOSED',
  'STOCK_INSUFFICIENT',
  'CREDIT_LIMIT_EXCEEDED',
  'APPROVAL_REQUIRED',
  'IDEMPOTENCY_MISMATCH',
  'INTERNAL_ERROR',
  'SERVICE_UNAVAILABLE',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

/**
 * Short, non-localised titles. RFC 7807 wants a summary that does not change
 * per occurrence, which is exactly what a fixed title is for.
 */
export const ERROR_TITLES: Record<ErrorCode, string> = {
  VALIDATION_FAILED: 'Validation failed',
  UNAUTHENTICATED: 'Authentication required',
  INVALID_CREDENTIALS: 'Invalid credentials',
  FORBIDDEN: 'Insufficient permissions',
  NOT_FOUND: 'Resource not found',
  CONFLICT: 'Conflicting state',
  PRECONDITION_FAILED: 'Precondition failed',
  PAYLOAD_TOO_LARGE: 'Payload too large',
  UNSUPPORTED_MEDIA_TYPE: 'Unsupported media type',
  RATE_LIMITED: 'Too many requests',
  VERSION_CONFLICT: 'Record was modified elsewhere',
  PERIOD_CLOSED: 'Accounting period is closed',
  STOCK_INSUFFICIENT: 'Insufficient stock',
  CREDIT_LIMIT_EXCEEDED: 'Credit limit exceeded',
  APPROVAL_REQUIRED: 'Approval required',
  IDEMPOTENCY_MISMATCH: 'Idempotency key reused with a different payload',
  INTERNAL_ERROR: 'Internal server error',
  SERVICE_UNAVAILABLE: 'Service unavailable',
};

const PROBLEM_TYPE_PREFIX = 'https://retailpro.dev/problems/';

export function problemTypeFor(code: ErrorCode): string {
  return `${PROBLEM_TYPE_PREFIX}${code.toLowerCase().replace(/_/g, '-')}`;
}

export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail: string;
  code: ErrorCode;
  /** Machine-readable field errors, present for VALIDATION_FAILED */
  errors?: Record<string, string[]>;
  correlationId?: string;
  timestamp: string;
}

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && (ERROR_CODES as readonly string[]).includes(value);
}

/**
 * Narrow an unparsed response body to a problem document.
 *
 * A non-2xx response is not guaranteed to be one: a reverse proxy returns an
 * HTML 502, and a typo'd base URL can return anything. Callers need a value
 * they can switch on without first proving the shape.
 */
export function asProblemDetails(body: unknown): ProblemDetails | null {
  if (typeof body !== 'object' || body === null) return null;
  const candidate = body as Partial<ProblemDetails>;
  if (!isErrorCode(candidate.code)) return null;
  return {
    type: typeof candidate.type === 'string' ? candidate.type : problemTypeFor(candidate.code),
    title: typeof candidate.title === 'string' ? candidate.title : ERROR_TITLES[candidate.code],
    status: typeof candidate.status === 'number' ? candidate.status : 500,
    detail: typeof candidate.detail === 'string' ? candidate.detail : 'Request failed.',
    code: candidate.code,
    ...(candidate.errors ? { errors: candidate.errors } : {}),
    ...(candidate.correlationId ? { correlationId: candidate.correlationId } : {}),
    timestamp:
      typeof candidate.timestamp === 'string' ? candidate.timestamp : new Date().toISOString(),
  };
}

/** Normalise `string | string[]` field errors into the wire shape. */
export function toFieldErrors(input: Record<string, string | string[]>): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const [key, value] of Object.entries(input)) {
    out[key] = Array.isArray(value) ? value : [value];
  }
  return out;
}

/** Flatten a Zod error into the `errors` field of a problem document. */
export function fieldErrorsFromIssues(
  issues: { path: (string | number | symbol)[]; message: string }[],
  prefix = 'query',
): Record<string, string[]> {
  const errors: Record<string, string[]> = {};
  for (const issue of issues) {
    const key = issue.path.map(String).join('.') || prefix;
    (errors[key] ??= []).push(issue.message);
  }
  return errors;
}
